import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';
import { getPlanIdFromProductId, getPlanById } from '../../utils/featureFlags';
import './SalesDashboard.css';

const SALES_FONT_LINK_ID = 'montserrat-sales-admin-font';

const normalize = (value) => (value ? String(value).trim().toLowerCase() : '');
const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const LEGACY_ALLOWED_SET = new Set(
  [
    'Soft Flow S.C.S',
    'prochart management sal',
    'Poultec',
    'Modern Industrial Products',
    'JSK Real Estate',
    'GlobeMed Group',
    'Egety',
    'Crepaway',
  ].map(normalize)
);

const HIDDEN_COMPANY_SET = new Set(
  [
    'blabla inc',
    'blabla123',
    'Confidential',
    'Jeff.co',
    'RemotelyX',
    'Test',
    'test',
    'Tipper network',
  ].map(normalize)
);

const LEGACY_CUTOFF = new Date('2023-01-01T00:00:00Z');

const SUBSCRIBED_STATUSES = new Set([
  'active',
  'unlimited',
  'past_due',
  'incomplete',
  'incomplete_payment',
  'paused',
]);

const CANCELED_STATUSES = new Set([
  'cancelled',
  'canceled',
  'incomplete_expired',
  'canceled_at_period_end',
]);

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const isSubscribedStatus = (status) => SUBSCRIBED_STATUSES.has(normalize(status));

const isCanceledStatus = (status) => {
  const normalized = normalize(status);
  if (!normalized) return false;
  if (CANCELED_STATUSES.has(normalized)) return true;
  return normalized.includes('cancel');
};

const isTrialingAccount = (item) => {
  const subStatus = normalize(item.subscriptionStatus);
  const trialStatus = normalize(item.trialStatus);
  const tseStatus = normalize(item.tseStatus);
  return subStatus.includes('trial') || trialStatus === 'active' || tseStatus.includes('trial');
};

const hasActiveFreeJobs = (item) => {
  const plan = normalize(item.planName);
  const subStatus = normalize(item.subscriptionStatus);
  const activeJobs = Number(item.activeJobs || 0);
  const isFreeStatus = !subStatus || subStatus === 'none' || subStatus === 'trial_expired';
  return activeJobs > 0 && (plan === 'free' || isFreeStatus);
};

const isSubscribedAccount = (item) =>
  isSubscribedStatus(item.subscriptionStatus) || isSubscribedStatus(item.tseStatus);

const isCanceledAccount = (item) =>
  isCanceledStatus(item.subscriptionStatus) || isCanceledStatus(item.tseStatus);

const isDormantAccount = (item) => {
  const lastLogin = parseDate(item.lastLogin);
  if (!lastLogin) return true;
  return Date.now() - lastLogin.getTime() > THIRTY_DAYS_MS;
};

const FILTERS = [
  { key: 'trialing', label: 'Trialing', predicate: isTrialingAccount },
  { key: 'activeFree', label: 'Active Free Jobs', predicate: hasActiveFreeJobs },
  { key: 'subscribed', label: 'Subscribed', predicate: isSubscribedAccount },
  { key: 'canceled', label: 'Canceled', predicate: isCanceledAccount },
  { key: 'inactiveMonth', label: 'Inactive 30+ Days', predicate: isDormantAccount },
];

const FILTER_LOOKUP = FILTERS.reduce((acc, filter) => {
  acc[filter.key] = filter;
  return acc;
}, {});

const uniqueDefined = (values) => {
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    if (typeof value !== 'string' || value.trim() === '') {
      return;
    }
    const trimmed = value.trim();
    if (!seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  });
  return result;
};

const resolveBackendCandidates = () => {
  const envCandidates = uniqueDefined([
    process.env.REACT_APP_BACKEND_URL_PROD,
    process.env.REACT_APP_BACKEND_URL_PROD,
  ]);

  if (envCandidates.length === 0) {
    return [''];
  }
  // Ensure we attempt both DEV and PROD by appending the opposite order as fallback
  const fallbacks = envCandidates.slice().reverse();
  const combined = [...envCandidates, ...fallbacks];
  combined.push('');
  return uniqueDefined(combined.concat(''));
};

const buildEndpointUrl = (base, path) => {
  const trimmedBase = base.replace(/\/+$/, '');
  if (!trimmedBase) {
    return path.startsWith('/') ? path : `/${path}`;
  }
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${cleanPath}`;
};

const sortableNumericKeys = new Set([
  'jobsPosted',
  'activeJobs',
  'candidatesSourced',
  'shortlists',
  'shortlistProfiles',
  'tseCreditsRemaining',
]);

const sortableDateKeys = new Set(['renewalDate', 'lastLogin', 'createdAt', 'trialExpiresAt']);

const SalesDashboard = () => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState([]);
  const [subsByUid, setSubsByUid] = useState({});
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'companyName', direction: 'asc' });
  const [unauthorized, setUnauthorized] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  useEffect(() => {
    if (!document.getElementById(SALES_FONT_LINK_ID)) {
      const link = document.createElement('link');
      link.id = SALES_FONT_LINK_ID;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    let isMounted = true;

    const fetchEmployers = async () => {
      try {
        setLoading(true);
        const token = await currentUser.getIdToken();

        const candidates = resolveBackendCandidates();
        let payload = null;
        let lastError = null;

        for (const base of candidates) {
          try {
            const endpoint = buildEndpointUrl(base, '/admin/employers');
            const response = await fetch(endpoint, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.status === 401 || response.status === 403) {
              if (isMounted) {
                setUnauthorized(true);
              }
              return;
            }

            if (!response.ok) {
              const message = await response.text();
              throw new Error(message || 'Failed to load employer records.');
            }

            payload = await response.json();
            break;
          } catch (candidateError) {
            lastError = candidateError;
          }
        }

        if (!payload) {
          throw lastError || new Error('Admin endpoint is not available.');
        }

        if (!isMounted) return;

        const rawEmployers = Array.isArray(payload.employers) ? payload.employers : [];
        const cleanedEmployers = rawEmployers.filter((item) => {
          const name = normalize(item?.companyName || '');
          if (HIDDEN_COMPANY_SET.has(name)) {
            return false;
          }

          const createdDate = parseDate(item?.createdAt);
          if (createdDate && createdDate < LEGACY_CUTOFF && !LEGACY_ALLOWED_SET.has(name)) {
            return false;
          }

          return true;
        });

        setEmployers(cleanedEmployers);
        // Fetch unified subscription from RTDB for each employer to ensure
        // we use productId → planId mapping for ATS/TSE/Bundle
        try {
          const entries = await Promise.all(
            cleanedEmployers.map(async (emp) => {
              try {
                const snap = await get(ref(database, `companies/${emp.uid}/subscription`));
                if (!snap.exists()) return [emp.uid, null];
                const raw = snap.val() || {};
                // Normalize from unified schema
                let { productType, productId, planId, status } = raw;
                // Derive planId from productId if needed
                if (!planId && productId) {
                  planId = getPlanIdFromProductId(productId);
                }
                // Derive productType from planId if missing
                if (!productType && typeof planId === 'string') {
                  if (planId.startsWith('ats_')) productType = 'ats';
                  else if (planId.startsWith('tse_')) productType = 'tse';
                  else if (planId.startsWith('bundle_')) productType = 'bundle';
                }
                // Resolve display plan name via featureFlags if possible
                let planName = '—';
                if (productType && planId) {
                  const plan = getPlanById(productType, planId);
                  planName = plan?.name || planId;
                }
                return [emp.uid, { productType, planId, planName, status, raw }];
              } catch {
                return [emp.uid, null];
              }
            })
          );
          const map = {};
          entries.forEach(([uid, sub]) => { map[uid] = sub; });
          setSubsByUid(map);
        } catch {
          // Non-fatal for dashboard; keep UI usable
        }
        setError(null);
      } catch (err) {
        if (!isMounted) return;
        console.error('Error loading sales admin data:', err);
        setError(err.message || 'Failed to load employer records.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEmployers();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  useEffect(() => {
    if (unauthorized) {
      navigate('/');
    }
  }, [unauthorized, navigate]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const formatDate = (isoString, includeTime = false) => {
    if (!isoString) return '—';
    try {
      const date = new Date(isoString);
      if (Number.isNaN(date.getTime())) {
        return '—';
      }
      if (includeTime) {
        return date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return '—';
    }
  };

  const normalizeStatus = (value) => {
    if (!value) return '—';
    const stripped = String(value).replace(/_/g, ' ').toLowerCase();
    return stripped.replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Derive a concise subscriptions summary from heterogeneous backend fields
  // We give equal importance to ATS, TSE and Bundle.
  const getSubscriptions = (item, unifiedSub) => {
    const subs = [];

    // Check if user is on free trial (trial covers everything)
    if (isTrialingAccount(item)) {
      subs.push({
        product: '',
        plan: 'Trial',
      });
      return subs;
    }

    const productType = normalize(unifiedSub?.productType || item.productType);

    // Bundle takes precedence and is shown as a single unified product
    if (productType === 'bundle' || unifiedSub?.planName?.toLowerCase().includes('bundle') || item.bundlePlan) {
      subs.push({
        product: 'Bundle',
        plan: unifiedSub?.planName || item.bundlePlan || '—',
      });
      return subs;
    }

    // ATS (if present)
    if (unifiedSub?.productType === 'ats' || item.planName) {
      subs.push({
        product: 'ATS',
        plan: unifiedSub?.planName || item.planName || '—',
      });
    }

    // TSE (if present)
    if (unifiedSub?.productType === 'tse' || item.tsePlan) {
      subs.push({
        product: 'TSE',
        plan: unifiedSub?.planName || item.tsePlan || '—',
      });
    }

    // If nothing was recognized, show Free
    if (subs.length === 0) {
      subs.push({ product: 'ATS', plan: 'Free' });
    }

    return subs;
  };

  // Resolve per-product status for display alongside subscriptions
  const getProductStatus = (item, product, unifiedSub) => {
    // If on trial, show trial status
    if (isTrialingAccount(item)) {
      return 'Active Trial';
    }

    const productType = normalize(unifiedSub?.productType || item.productType);

    if (product === 'Bundle' || productType === 'bundle') {
      // Prefer unified sub status, then fall back to legacy fields
      return normalizeStatus(unifiedSub?.status || item.bundleStatus || item.subscriptionStatus || item.tseStatus || '—');
    }
    if (product === 'ATS') {
      return normalizeStatus(unifiedSub?.status || item.subscriptionStatus || '—');
    }
    if (product === 'TSE') {
      return normalizeStatus(unifiedSub?.status || item.tseStatus || '—');
    }
    return '—';
  };

  const toggleFilter = (key) => {
    setActiveFilter((prev) => (prev === key ? null : key));
  };

  const filteredEmployers = useMemo(() => {
    const base = Array.isArray(employers) ? employers : [];
    const query = searchQuery.trim().toLowerCase();

    const searchFiltered = query
      ? base.filter((item) => {
        const email = (item.email || '').toLowerCase();
        const company = (item.companyName || '').toLowerCase();
        return email.includes(query) || company.includes(query);
      })
      : base;

    const filtered =
      activeFilter === null
        ? searchFiltered
        : searchFiltered.filter((item) => {
          const definition = FILTER_LOOKUP[activeFilter];
          return definition ? definition.predicate(item) : true;
        });

    if (!sortConfig || !sortConfig.key) {
      return filtered;
    }

    const { key, direction } = sortConfig;
    const multiplier = direction === 'asc' ? 1 : -1;

    const getComparable = (item) => {
      const value = item?.[key];
      if (sortableNumericKeys.has(key)) {
        return Number(value) || 0;
      }
      if (sortableDateKeys.has(key)) {
        return value ? new Date(value).getTime() || 0 : 0;
      }
      return value ? String(value).toLowerCase() : '';
    };

    return [...filtered].sort((a, b) => {
      const aValue = getComparable(a);
      const bValue = getComparable(b);
      if (aValue < bValue) return -1 * multiplier;
      if (aValue > bValue) return 1 * multiplier;
      return 0;
    });
  }, [employers, searchQuery, sortConfig, activeFilter]);

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  return (
    <div className="sales-dashboard">
      <div className="sales-dashboard__header">
        <div className="sales-dashboard__title">
          <h1>{uiText("Sales Admin Dashboard")}</h1>
          <div className="sales-dashboard__tooltip" aria-label={uiText("Legacy accounts note")}>
            <span>?</span>
            <div className="sales-dashboard__tooltip-content">{uiText("This portal excludes extremely old accounts from the early days of JOBSEEQR.")}</div>
          </div>
        </div>
        <p className="sales-dashboard__subtitle">{uiText("Consolidated employer accounts with subscription and usage insights.")}</p>
      </div>

      <div className="sales-dashboard__controls">
        <input
          type="text"
          placeholder={uiText("Search by email or company name")}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="sales-dashboard__search"
        />
      </div>

      <div className="sales-dashboard__filters">
        {FILTERS.map((filter) => {
          const isActive = activeFilter === filter.key;
          return (
            <button
              key={filter.key}
              type="button"
              className={`sales-dashboard__filter-btn ${isActive ? 'active' : ''}`}
              onClick={() => toggleFilter(filter.key)}
            >
              {uiText(filter.label)}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="sales-dashboard__state">{uiText("Loading employer records…")}</div>
      )}

      {!loading && error && (
        <div className="sales-dashboard__state sales-dashboard__state--error">
          {uiText(error)}
        </div>
      )}

      {!loading && !error && filteredEmployers.length === 0 && (
        <div className="sales-dashboard__state">{uiText("No employer records match your search.")}</div>
      )}

      {!loading && !error && filteredEmployers.length > 0 && (
        <div className="sales-dashboard__table-wrapper">
          <table className="sales-dashboard__table">
            <thead>
              <tr>
                <th onClick={() => handleSort('companyName')}>{uiText("Company ")}{renderSortIndicator('companyName')}
                </th>
                <th onClick={() => handleSort('email')}>{uiText("Email ")}{renderSortIndicator('email')}
                </th>
                <th onClick={() => handleSort('planName')}>{uiText("Subscriptions ")}{renderSortIndicator('planName')}
                </th>
                <th onClick={() => handleSort('subscriptionStatus')}>{uiText("Status ")}{renderSortIndicator('subscriptionStatus')}
                </th>
                <th onClick={() => handleSort('renewalDate')}>{uiText("Renewal Date ")}{renderSortIndicator('renewalDate')}
                </th>
                <th onClick={() => handleSort('jobsPosted')}>{uiText("Jobs Posted ")}{renderSortIndicator('jobsPosted')}
                </th>
                <th onClick={() => handleSort('candidatesSourced')}>{uiText("Candidates Sourced ")}{renderSortIndicator('candidatesSourced')}
                </th>
                <th onClick={() => handleSort('lastLogin')}>{uiText("Last Login ")}{renderSortIndicator('lastLogin')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployers.map((employer) => (
                <tr key={employer.uid}>
                  <td>
                    <div className="table-cell__primary">{employer.companyName || '—'}</div>
                    <div className="table-cell__secondary">{uiText("Joined ")}{formatDate(employer.createdAt)}
                    </div>
                  </td>
                  <td>{employer.email || '—'}</td>
                  <td>
                    {getSubscriptions(employer, subsByUid[employer.uid]).map((sub, idx) => (
                      <div key={sub.product || idx} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                        {sub.product && <div style={{ minWidth: 58, fontWeight: 600 }}>{sub.product}:</div>}
                        <div className="table-cell__primary" style={{ marginRight: 8 }}>{sub.plan}</div>
                        {sub.product === 'TSE' && typeof employer.tseCreditsRemaining === 'number' && (
                          <div className="table-cell__secondary">{uiText("Credits ")}{employer.tseCreditsRemaining}</div>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    {getSubscriptions(employer, subsByUid[employer.uid]).map((sub, idx) => (
                      <div key={`${sub.product || idx}-status`} style={{ display: 'flex', gap: 8 }}>
                        {sub.product && <div style={{ minWidth: 58, fontWeight: 600 }}>{sub.product}:</div>}
                        <div className="table-cell__primary">{getProductStatus(employer, sub.product, subsByUid[employer.uid])}</div>
                      </div>
                    ))}
                  </td>
                  <td>{formatDate(employer.renewalDate)}</td>
                  <td>
                    <div className="table-cell__primary">{employer.jobsPosted ?? 0}</div>
                    <div className="table-cell__secondary">{uiText("Active ")}{employer.activeJobs ?? 0}
                    </div>
                  </td>
                  <td>
                    <div className="table-cell__primary">{employer.candidatesSourced ?? 0}</div>
                    <div className="table-cell__secondary">{uiText("Shortlists ")}{employer.shortlists ?? 0}
                    </div>
                  </td>
                  <td>{formatDate(employer.lastLogin, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SalesDashboard;
